// OptimalAvailability Studio - Part 1 (Lines 1-1500)
// Comprehensive React application for reliability engineering
// Includes UI components, data models, undo/redo, menus, toolbars, and entity management

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useReducer,
  createContext,
  useContext
} from 'react';

import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Copy,
  Edit2,
  Settings,
  Grid3x3,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Play,
  Square,
  Save,
  File,
  FolderOpen,
  Search,
  Filter,
  Menu,
  X,
  MoreVertical,
  GripVertical,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  Package,
  Users,
  DollarSign,
  TrendingUp,
  GitBranch,
  Layers,
  BarChart3,
  Cog,
  HelpCircle,
  Download,
  Upload,
  RotateCcw,
  RotateCw,
  Minimize2,
  Home,
  Server,
  Hash,
  BookOpen,
  Briefcase,
  AlertTriangle,
  Fan,
  Target,
  Shield,
  Eye,
  EyeOff,
  SlidersHorizontal,
  Wrench
} from 'lucide-react';

// Import engines (these will be created separately)
// import { SimulationEngine } from './engine/simulation';
// import { FaultTreeEngine } from './engine/faultTreeEngine';
// import { WeibullAnalysis } from './engine/weibullEngine';
// import { LCCEngine } from './engine/lccEngine';
// import { generateFailureTime, weibullPDF, weibullCDF, weibullReliability, normalPDF, SeededRandom } from './engine/distributions';

// ============================================================================
// BRANDING CONSTANTS
// ============================================================================
const COLORS = {
  NAVY: '#002F65',        // Optimal® Dark Blue — RGB(0, 47, 101)
  GOLD: '#00A8CB',        // Optimal® Light Blue — RGB(0, 168, 203)
  BLUE: '#002F65',        // Alias for Dark Blue (primary actions)
  TEAL: '#00A8CB',        // Optimal® Light Blue (accent)
  TEAL_LIGHT: '#D4F1F9',  // Optimal® Light Blue at ~15% — clean blue wash
  TEAL_MID: '#66C7DD',    // Optimal® Light Blue at ~60%
  LIGHT_BG: '#F7F8FA',    // Clean neutral light grey background
  PANEL_BG: '#FFFFFF',    // Clean white panels
  BORDER: '#D1D5DB',      // Neutral grey borders
  BORDER_TEAL: '#80D4E5', // Light Blue tinted border for focus states
  TEXT: '#0F172A',         // Slate-900 — strong contrast text
  TEXT_DIM: '#475569',     // Slate-600 — secondary text
  SUCCESS: '#00A8CB',      // Optimal® Light Blue (positive/active)
  ERROR: '#002F65',        // Optimal® Dark Blue (alert/failed)
  WARNING: '#6698C2',      // Mid-blue (caution/standby)
  INFO: '#00A8CB'          // Optimal® Light Blue
};

// ============================================================================
// TREE SECTION DEFINITIONS — MECE Asset Hierarchy
// Grouped by domain: Asset Hierarchy, Analysis, Strategy, Resources, Modelling, Commercial
// ============================================================================
const TREE_GROUPS = [
  {
    id: 'assetHierarchy',
    label: 'Asset Hierarchy',
    icon: Home,
    color: '#002F65',
    sections: [
      { id: 'locations', label: 'Locations', icon: Home, color: '#00A8CB', parent: true },
      { id: 'equipments', label: 'Equipment', icon: Zap, color: '#002F65', parent: true }
    ]
  },
  {
    id: 'analysis',
    label: 'Failure Analysis',
    icon: AlertTriangle,
    color: '#B91C1C',
    sections: [
      { id: 'failuremodels', label: 'Failure Modes', icon: AlertTriangle, color: '#00A8CB' },
      { id: 'consequences', label: 'Consequences', icon: AlertCircle, color: '#002F65' },
      { id: 'rcmcauses', label: 'RCM Causes', icon: Target, color: '#00A8CB' },
      { id: 'weibullsets', label: 'Weibull Sets', icon: TrendingUp, color: '#002F65' }
    ]
  },
  {
    id: 'strategy',
    label: 'Maintenance Strategy',
    icon: Wrench,
    color: '#2A9D8F',
    sections: [
      { id: 'scheduledtasks', label: 'Scheduled Tasks', icon: Clock, color: '#00A8CB' },
      { id: 'correctivetasks', label: 'Corrective Tasks', icon: Wrench, color: '#002F65' },
      { id: 'taskgroups', label: 'Task Groups', icon: Layers, color: '#00A8CB' }
    ]
  },
  {
    id: 'resources',
    label: 'Resources',
    icon: Package,
    color: '#00A8CB',
    sections: [
      { id: 'spares', label: 'Spare Parts', icon: Package, color: '#00A8CB' },
      { id: 'labor', label: 'Labour', icon: Users, color: '#002F65' },
      { id: 'equipmentresources', label: 'Tools & Equipment', icon: Cog, color: '#00A8CB' },
      { id: 'boms', label: 'Bills of Materials', icon: Server, color: '#002F65' }
    ]
  },
  {
    id: 'modelling',
    label: 'System Modelling',
    icon: GitBranch,
    color: '#002F65',
    sections: [
      { id: 'rbdblocks', label: 'RBD Blocks', icon: GitBranch, color: '#002F65' },
      { id: 'gates', label: 'FT Gates', icon: BarChart3, color: '#00A8CB' },
      { id: 'primaryevents', label: 'Primary Events', icon: Zap, color: '#002F65' },
      { id: 'eventtrees', label: 'Event Trees', icon: GitBranch, color: '#002F65' },
      { id: 'phases', label: 'Phases', icon: Clock, color: '#00A8CB' }
    ]
  },
  {
    id: 'commercial',
    label: 'Commercial',
    icon: DollarSign,
    color: '#92400E',
    sections: [
      { id: 'costnodes', label: 'Cost Nodes', icon: DollarSign, color: '#002F65' },
      { id: 'products', label: 'Products', icon: Package, color: '#002F65' },
      { id: 'rules', label: 'Rules', icon: Cog, color: '#00A8CB' }
    ]
  }
];

// Flat list for backward compatibility (used by DataGrid column selects, etc.)
const TREE_SECTIONS = TREE_GROUPS.flatMap(g => g.sections);

// ============================================================================
// COLUMN DEFINITIONS FOR ALL ENTITY TYPES
// ============================================================================
const COLUMN_DEFINITIONS = {
  locations: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'parent', label: 'Parent', type: 'select', width: 120 },
    { key: 'hierarchyLevel', label: 'Level', type: 'number', width: 80, editable: false },
    { key: 'operationalRule', label: 'Op. Rule', type: 'text', width: 150 },
    { key: 'phaseAssignment', label: 'Phase', type: 'select', width: 120 }
  ],
  equipments: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'type', label: 'Type', type: 'select', width: 100 },
    { key: 'location', label: 'Location', type: 'select', width: 120 },
    { key: 'redundancyType', label: 'Redundancy', type: 'select', width: 100 },
    { key: 'maxCapacity', label: 'Capacity', type: 'number', width: 80 },
    { key: 'state', label: 'State', type: 'select', width: 100, editable: false }
  ],
  failuremodels: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'distribution', label: 'Distribution', type: 'select', width: 120 },
    { key: 'eta', label: 'η (Eta)', type: 'number', width: 80 },
    { key: 'beta', label: 'β (Beta)', type: 'number', width: 80 },
    { key: 'mttf', label: 'MTTF', type: 'number', width: 80, editable: false },
    { key: 'mttr', label: 'MTTR', type: 'number', width: 80 }
  ],
  consequences: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'costRate', label: 'Cost Rate', type: 'number', width: 100 },
    { key: 'costPerOccurrence', label: 'Cost/Occ', type: 'number', width: 100 },
    { key: 'capacityLoss', label: 'Cap. Loss %', type: 'number', width: 80 },
    { key: 'safetyCrit', label: 'Safety', type: 'checkbox', width: 60 },
    { key: 'opCrit', label: 'Operational', type: 'checkbox', width: 60 }
  ],
  spares: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'stockLevel', label: 'Stock', type: 'number', width: 80 },
    { key: 'reorderPoint', label: 'Reorder Point', type: 'number', width: 100 },
    { key: 'unitCost', label: 'Unit Cost', type: 'number', width: 100 },
    { key: 'leadTime', label: 'Lead Time', type: 'number', width: 80 },
    { key: 'poolType', label: 'Pool Type', type: 'select', width: 100 }
  ],
  labor: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'costRate', label: 'Rate/Hour', type: 'number', width: 100 },
    { key: 'available', label: 'Available', type: 'number', width: 80 },
    { key: 'shift', label: 'Shift', type: 'select', width: 80 },
    { key: 'craftSkill', label: 'Skill', type: 'text', width: 120 },
    { key: 'crewSize', label: 'Crew Size', type: 'number', width: 80 },
    { key: 'mobilisationTime', label: 'Mobilisation (h)', type: 'number', width: 100 },
    { key: 'availabilitySchedule', label: 'Schedule', type: 'select', width: 100 },
    { key: 'skillCategory', label: 'Skill Category', type: 'select', width: 120 },
    { key: 'utilisationTarget', label: 'Util. Target %', type: 'number', width: 100 },
    { key: 'overtimeRate', label: 'OT Rate/Hr', type: 'number', width: 100 }
  ],
  equipmentresources: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'resourceType', label: 'Resource Type', type: 'select', width: 120 },
    { key: 'quantity', label: 'Quantity', type: 'number', width: 80 },
    { key: 'unitCost', label: 'Unit Cost', type: 'number', width: 100 },
    { key: 'availabilitySchedule', label: 'Schedule', type: 'select', width: 100 },
    { key: 'mobilisationTime', label: 'Mobilisation (h)', type: 'number', width: 100 },
    { key: 'assignedLocation', label: 'Location', type: 'select', width: 120 }
  ],
  scheduledtasks: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'interval', label: 'Interval (h)', type: 'number', width: 100 },
    { key: 'duration', label: 'Duration (h)', type: 'number', width: 100 },
    { key: 'cost', label: 'Cost', type: 'number', width: 80 },
    { key: 'taskType', label: 'Type', type: 'select', width: 100 },
    { key: 'asGoodAsNew', label: 'As-Good-As-New', type: 'checkbox', width: 100 }
  ],
  correctivetasks: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'failureModel', label: 'Failure Mode', type: 'select', width: 120 },
    { key: 'repairDuration', label: 'Repair Time (h)', type: 'number', width: 100 },
    { key: 'repairDistribution', label: 'Repair Dist.', type: 'select', width: 120 },
    { key: 'laborReq', label: 'Labor Req.', type: 'select', width: 100 },
    { key: 'spareReq', label: 'Spare Req.', type: 'select', width: 100 },
    { key: 'repairCost', label: 'Cost', type: 'number', width: 80 },
    { key: 'holdForRepair', label: 'Hold', type: 'checkbox', width: 60 },
    { key: 'logisticDelay', label: 'Logistic Delay (h)', type: 'number', width: 100 }
  ],
  taskgroups: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'tasks', label: 'Task Count', type: 'number', width: 80, editable: false },
    { key: 'groupScheduleRule', label: 'Schedule Rule', type: 'text', width: 150 },
    { key: 'combinedDowntime', label: 'Combined DT', type: 'number', width: 100 }
  ],
  rbdblocks: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'blockType', label: 'Type', type: 'select', width: 100 },
    { key: 'failureModel', label: 'Failure Mode', type: 'select', width: 120 },
    { key: 'state', label: 'State', type: 'select', width: 100, editable: false }
  ],
  gates: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'gateType', label: 'Gate Type', type: 'select', width: 100 },
    { key: 'inputs', label: 'Input Count', type: 'number', width: 80, editable: false }
  ],
  primaryevents: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'eventType', label: 'Type', type: 'select', width: 120 },
    { key: 'probability', label: 'Probability', type: 'number', width: 100 },
    { key: 'failureModel', label: 'Failure Mode', type: 'select', width: 120 }
  ],
  phases: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'startTime', label: 'Start', type: 'number', width: 80 },
    { key: 'endTime', label: 'End', type: 'number', width: 80 },
    { key: 'failureRateMultiplier', label: 'FR Mult.', type: 'number', width: 100 }
  ],
  weibullsets: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'failureData', label: 'Failures', type: 'number', width: 80 },
    { key: 'beta', label: 'β', type: 'number', width: 80, editable: false },
    { key: 'eta', label: 'η', type: 'number', width: 80, editable: false },
    { key: 'rSquared', label: 'R²', type: 'number', width: 80, editable: false }
  ],
  boms: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'items', label: 'Item Count', type: 'number', width: 80, editable: false },
    { key: 'costs', label: 'Total Cost', type: 'number', width: 100 }
  ],
  products: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'nominalCapacity', label: 'Nominal Cap.', type: 'number', width: 120 },
    { key: 'targetCapacity', label: 'Target Cap.', type: 'number', width: 120 },
    { key: 'penaltyRate', label: 'Penalty Rate', type: 'number', width: 100 }
  ],
  rules: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'ruleType', label: 'Type', type: 'select', width: 100 },
    { key: 'triggerCondition', label: 'Condition', type: 'text', width: 150 },
    { key: 'action', label: 'Action', type: 'text', width: 150 }
  ],
  costnodes: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'costCategory', label: 'Category', type: 'select', width: 120 },
    { key: 'annualCost', label: 'Annual Cost', type: 'number', width: 120 },
    { key: 'escalationRate', label: 'Escalation %', type: 'number', width: 100 }
  ],
  rcmcauses: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'failureMode', label: 'Failure Mode', type: 'text', width: 150 },
    { key: 'severity', label: 'Severity', type: 'number', width: 80 },
    { key: 'rpn', label: 'RPN', type: 'number', width: 60, editable: false }
  ],
  eventtrees: [
    { key: 'id', label: 'ID', type: 'text', width: 100, editable: false },
    { key: 'description', label: 'Description', type: 'text', width: 200 },
    { key: 'initiatingEvent', label: 'Initiating Event', type: 'select', width: 150 },
    { key: 'branches', label: 'Branches', type: 'number', width: 80, editable: false }
  ]
};

// ============================================================================
// DEMO DATA GENERATOR
// ============================================================================
function createDemoData() {
  return {
    locations: [
      {
        id: 'LOC-001',
        description: 'Plant A - Main Production',
        parent: null,
        hierarchyLevel: 0,
        operationalRule: 'Continuous 24/7 operation',
        phaseAssignment: 'PHASE-001'
      },
      {
        id: 'LOC-002',
        description: 'Production Line 1',
        parent: 'LOC-001',
        hierarchyLevel: 1,
        operationalRule: '3-shift operation',
        phaseAssignment: 'PHASE-001'
      },
      {
        id: 'LOC-003',
        description: 'Production Line 2',
        parent: 'LOC-001',
        hierarchyLevel: 1,
        operationalRule: '3-shift operation',
        phaseAssignment: 'PHASE-001'
      },
      {
        id: 'LOC-004',
        description: 'Maintenance Shop',
        parent: 'LOC-001',
        hierarchyLevel: 1,
        operationalRule: 'Business hours',
        phaseAssignment: 'PHASE-001'
      },
      {
        id: 'LOC-005',
        description: 'Warehouse',
        parent: 'LOC-001',
        hierarchyLevel: 1,
        operationalRule: 'Business hours',
        phaseAssignment: 'PHASE-001'
      },
      {
        id: 'LOC-006',
        description: 'Plant B - Secondary',
        parent: null,
        hierarchyLevel: 0,
        operationalRule: 'Standby operation',
        phaseAssignment: 'PHASE-002'
      }
    ],
    equipments: [
      {
        id: 'EQP-001',
        description: 'Primary Pump A1',
        type: 'Centrifugal Pump',
        location: 'LOC-002',
        redundancyType: 'parallel',
        kValue: null,
        nValue: 2,
        standbyMode: 'cold',
        switchingDelay: 0.5,
        maxCapacity: 1000,
        state: 'operating'
      },
      {
        id: 'EQP-002',
        description: 'Primary Pump A2',
        type: 'Centrifugal Pump',
        location: 'LOC-002',
        redundancyType: 'parallel',
        kValue: null,
        nValue: 2,
        standbyMode: 'cold',
        switchingDelay: 0.5,
        maxCapacity: 1000,
        state: 'standby'
      },
      {
        id: 'EQP-003',
        description: 'Compressor Unit B',
        type: 'Rotary Compressor',
        location: 'LOC-003',
        redundancyType: 'series',
        kValue: null,
        nValue: null,
        standbyMode: null,
        switchingDelay: 0,
        maxCapacity: 800,
        state: 'operating'
      },
      {
        id: 'EQP-004',
        description: 'Control System PLC',
        type: 'Programmable Logic Controller',
        location: 'LOC-002',
        redundancyType: 'series',
        kValue: null,
        nValue: null,
        standbyMode: null,
        switchingDelay: 0,
        maxCapacity: 100,
        state: 'operating'
      },
      {
        id: 'EQP-005',
        description: 'Filter Unit F1',
        type: 'Filtration Unit',
        location: 'LOC-003',
        redundancyType: 'kofn',
        kValue: 2,
        nValue: 3,
        standbyMode: null,
        switchingDelay: 0,
        maxCapacity: 500,
        state: 'operating'
      }
    ],
    failuremodels: [
      {
        id: 'FM-001',
        description: 'Bearing Wear - Weibull',
        distribution: 'Weibull',
        eta: 8760,
        beta: 1.8,
        gamma: 100,
        mttf: 4850,
        lambda: 0.000206,
        mu: null,
        sigma: null,
        repairDistribution: 'LogNormal',
        mttr: 24,
        repairEta: 20,
        repairBeta: 0.8,
        conditionalProbability: 1.0
      },
      {
        id: 'FM-002',
        description: 'Seal Failure - Exponential',
        distribution: 'Exponential',
        eta: 5000,
        beta: 1.0,
        gamma: 0,
        mttf: 5000,
        lambda: 0.0002,
        mu: null,
        sigma: null,
        repairDistribution: 'Fixed',
        mttr: 8,
        repairEta: 8,
        repairBeta: 0,
        conditionalProbability: 1.0
      },
      {
        id: 'FM-003',
        description: 'Corrosion Degradation',
        distribution: 'Lognormal',
        eta: 7200,
        beta: 0.9,
        gamma: 50,
        mttf: 2000,
        lambda: null,
        mu: 8.5,
        sigma: 0.6,
        repairDistribution: 'LogNormal',
        mttr: 48,
        repairEta: 40,
        repairBeta: 0.9,
        conditionalProbability: 0.8
      },
      {
        id: 'FM-004',
        description: 'Electrical Short - Constant',
        distribution: 'Fixed',
        eta: 3000,
        beta: null,
        gamma: null,
        mttf: 3000,
        lambda: 0.000333,
        mu: null,
        sigma: null,
        repairDistribution: 'Fixed',
        mttr: 4,
        repairEta: 4,
        repairBeta: 0,
        conditionalProbability: 1.0
      },
      {
        id: 'FM-005',
        description: 'Vibration-Induced Failure',
        distribution: 'BiWeibull',
        eta: 6500,
        beta: 2.1,
        gamma: 200,
        mttf: 5600,
        lambda: null,
        mu: null,
        sigma: null,
        repairDistribution: 'LogNormal',
        mttr: 36,
        repairEta: 32,
        repairBeta: 0.85,
        conditionalProbability: 0.95
      }
    ],
    consequences: [
      {
        id: 'CONS-001',
        description: 'Loss of Production - Full',
        costRate: 5000,
        costPerOccurrence: 15000,
        capacityLoss: 100,
        safetyCrit: false,
        opCrit: true,
        envCrit: false,
        productStream: 'Primary Product',
        silLevel: 2
      },
      {
        id: 'CONS-002',
        description: 'Partial Production Loss',
        costRate: 2500,
        costPerOccurrence: 7500,
        capacityLoss: 50,
        safetyCrit: false,
        opCrit: true,
        envCrit: false,
        productStream: 'Primary Product',
        silLevel: 1
      },
      {
        id: 'CONS-003',
        description: 'Safety Hazard - High',
        costRate: 10000,
        costPerOccurrence: 50000,
        capacityLoss: 100,
        safetyCrit: true,
        opCrit: true,
        envCrit: false,
        productStream: null,
        silLevel: 3
      },
      {
        id: 'CONS-004',
        description: 'Environmental Release',
        costRate: 8000,
        costPerOccurrence: 100000,
        capacityLoss: 50,
        safetyCrit: false,
        opCrit: true,
        envCrit: true,
        productStream: null,
        silLevel: 2
      }
    ],
    spares: [
      {
        id: 'SPR-001',
        description: 'Bearing Assembly - Type A',
        stockLevel: 5,
        reorderPoint: 2,
        reorderQuantity: 10,
        leadTime: 7,
        unitCost: 850,
        storageCost: 50,
        shelfLife: 36,
        poolType: 'pooled',
        replenishmentPolicy: 'continuous'
      },
      {
        id: 'SPR-002',
        description: 'Mechanical Seal Kit',
        stockLevel: 8,
        reorderPoint: 3,
        reorderQuantity: 12,
        leadTime: 5,
        unitCost: 600,
        storageCost: 30,
        shelfLife: 48,
        poolType: 'pooled',
        replenishmentPolicy: 'continuous'
      },
      {
        id: 'SPR-003',
        description: 'Motor Winding - 15kW',
        stockLevel: 2,
        reorderPoint: 1,
        reorderQuantity: 5,
        leadTime: 14,
        unitCost: 3500,
        storageCost: 100,
        shelfLife: 60,
        poolType: 'dedicated',
        replenishmentPolicy: 'periodic'
      },
      {
        id: 'SPR-004',
        description: 'Control Board PCB',
        stockLevel: 10,
        reorderPoint: 4,
        reorderQuantity: 20,
        leadTime: 10,
        unitCost: 1200,
        storageCost: 40,
        shelfLife: 24,
        poolType: 'pooled',
        replenishmentPolicy: 'continuous'
      },
      {
        id: 'SPR-005',
        description: 'Gasket Set Standard',
        stockLevel: 15,
        reorderPoint: 5,
        reorderQuantity: 30,
        leadTime: 3,
        unitCost: 120,
        storageCost: 10,
        shelfLife: 36,
        poolType: 'pooled',
        replenishmentPolicy: 'continuous'
      }
    ],
    labor: [
      {
        id: 'LBR-001',
        description: 'Senior Technician - Mechanical',
        costRate: 85,
        available: 40,
        shift: '08:00-16:30',
        craftSkill: 'Mechanical Maintenance',
        calloutCost: 250,
        travelTime: 30,
        crewSize: 1,
        laborPool: 'Mechanical',
        calendar: 'Business Days'
      },
      {
        id: 'LBR-002',
        description: 'Electrician - Lead',
        costRate: 95,
        available: 40,
        shift: '08:00-16:30',
        craftSkill: 'Electrical/Controls',
        calloutCost: 300,
        travelTime: 20,
        crewSize: 1,
        laborPool: 'Electrical',
        calendar: 'Business Days'
      },
      {
        id: 'LBR-003',
        description: 'Technician - General',
        costRate: 65,
        available: 40,
        shift: '08:00-16:30',
        craftSkill: 'General Maintenance',
        calloutCost: 180,
        travelTime: 30,
        crewSize: 2,
        laborPool: 'General',
        calendar: '24/7'
      },
      {
        id: 'LBR-004',
        description: 'Shift Supervisor',
        costRate: 75,
        available: 40,
        shift: '16:00-00:30',
        craftSkill: 'Operations/Supervision',
        calloutCost: 200,
        travelTime: 15,
        crewSize: 1,
        laborPool: 'Supervision',
        calendar: '24/7'
      }
    ],
    equipmentresources: [
      {
        id: 'EQR-001',
        description: 'Hydraulic Pump - Mobile Unit',
        resourceType: 'Hydraulic Equipment',
        quantity: 2,
        unitCost: 15000,
        availabilitySchedule: 'Business Days',
        mobilisationTime: 4,
        assignedLocation: 'PUMP-1'
      },
      {
        id: 'EQR-002',
        description: 'Portable Welding Machine',
        resourceType: 'Welding Equipment',
        quantity: 1,
        unitCost: 8500,
        availabilitySchedule: '24/7',
        mobilisationTime: 2,
        assignedLocation: 'SHOP-A'
      },
      {
        id: 'EQR-003',
        description: 'Diagnostic Test Kit',
        resourceType: 'Diagnostic',
        quantity: 3,
        unitCost: 5200,
        availabilitySchedule: 'Business Days',
        mobilisationTime: 1,
        assignedLocation: 'MAINT-1'
      },
      {
        id: 'EQR-004',
        description: 'Lifting Equipment - 20T Crane',
        resourceType: 'Lifting Equipment',
        quantity: 1,
        unitCost: 45000,
        availabilitySchedule: 'Business Days',
        mobilisationTime: 3,
        assignedLocation: 'YARD'
      }
    ],
    scheduledtasks: [
      {
        id: 'ST-001',
        description: 'Bearing Inspection - Monthly',
        interval: 730,
        duration: 4,
        cost: 500,
        failureModel: 'FM-001',
        taskType: 'inspection',
        laborReq: 'LBR-001',
        spareReq: null,
        opportunistic: false,
        conditionBased: true,
        asGoodAsNew: false,
        rampTime: 0.5
      },
      {
        id: 'ST-002',
        description: 'Seal Replacement - Preventive',
        interval: 4380,
        duration: 8,
        cost: 1200,
        failureModel: 'FM-002',
        taskType: 'replacement',
        laborReq: 'LBR-001',
        spareReq: 'SPR-002',
        opportunistic: true,
        conditionBased: false,
        asGoodAsNew: true,
        rampTime: 1
      },
      {
        id: 'ST-003',
        description: 'Equipment Overhaul - Quarterly',
        interval: 2190,
        duration: 16,
        cost: 3000,
        failureModel: 'FM-001',
        taskType: 'overhaul',
        laborReq: 'LBR-001',
        spareReq: 'SPR-001',
        opportunistic: false,
        conditionBased: false,
        asGoodAsNew: true,
        rampTime: 2
      },
      {
        id: 'ST-004',
        description: 'Control System Firmware Update',
        interval: 8760,
        duration: 2,
        cost: 800,
        failureModel: 'FM-004',
        taskType: 'inspection',
        laborReq: 'LBR-002',
        spareReq: null,
        opportunistic: false,
        conditionBased: false,
        asGoodAsNew: false,
        rampTime: 0.25
      }
    ],
    correctivetasks: [
      {
        id: 'CT-001',
        description: 'Replace Failed Bearing',
        failureModel: 'FM-001',
        repairDuration: 6,
        repairDistribution: 'LogNormal',
        laborReq: 'LBR-001',
        spareReq: 'SPR-001',
        repairCost: 2500,
        holdForRepair: false,
        logisticDelay: 2
      },
      {
        id: 'CT-002',
        description: 'Replace Seal Assembly',
        failureModel: 'FM-002',
        repairDuration: 4,
        repairDistribution: 'Fixed',
        laborReq: 'LBR-001',
        spareReq: 'SPR-002',
        repairCost: 1800,
        holdForRepair: false,
        logisticDelay: 1
      },
      {
        id: 'CT-003',
        description: 'Electrical Repair/Replacement',
        failureModel: 'FM-004',
        repairDuration: 3,
        repairDistribution: 'Fixed',
        laborReq: 'LBR-002',
        spareReq: 'SPR-004',
        repairCost: 3000,
        holdForRepair: true,
        logisticDelay: 4
      }
    ],
    taskgroups: [
      {
        id: 'TG-001',
        description: 'Monthly Maintenance Group',
        tasks: 3,
        groupScheduleRule: 'First Monday of month',
        combinedDowntime: 12
      },
      {
        id: 'TG-002',
        description: 'Seasonal Equipment Inspection',
        tasks: 5,
        groupScheduleRule: 'Quarterly (Jan, Apr, Jul, Oct)',
        combinedDowntime: 24
      },
      {
        id: 'TG-003',
        description: 'Opportunistic Maintenance Bundle',
        tasks: 2,
        groupScheduleRule: 'When equipment down for any reason',
        combinedDowntime: 8
      }
    ],
    rbdblocks: [
      {
        id: 'RBD-001',
        description: 'Primary Pump System',
        failureModel: 'FM-001',
        page: 1,
        x: 100,
        y: 100,
        width: 80,
        height: 40,
        blockType: 'parallel',
        kValue: null,
        nValue: 2,
        capacity: 1000,
        logicRule: 'Any 1 of 2',
        subDiagramId: null,
        state: 'operating'
      },
      {
        id: 'RBD-002',
        description: 'Control System',
        failureModel: 'FM-004',
        page: 1,
        x: 250,
        y: 100,
        width: 80,
        height: 40,
        blockType: 'series',
        kValue: null,
        nValue: null,
        capacity: 100,
        logicRule: 'Series',
        subDiagramId: null,
        state: 'operating'
      },
      {
        id: 'RBD-003',
        description: 'Compressor Unit',
        failureModel: 'FM-005',
        page: 1,
        x: 400,
        y: 100,
        width: 80,
        height: 40,
        blockType: 'series',
        kValue: null,
        nValue: null,
        capacity: 800,
        logicRule: 'Series',
        subDiagramId: null,
        state: 'operating'
      }
    ],
    gates: [
      {
        id: 'GT-001',
        description: 'System Failure Gate',
        gateType: 'OR',
        kValue: null,
        nValue: null,
        inputs: ['GT-002', 'GT-003', 'PE-004'],
        parentGate: null,
        transferTo: null
      },
      {
        id: 'GT-002',
        description: 'Safety Hazard Gate',
        gateType: 'AND',
        kValue: null,
        nValue: null,
        inputs: ['PE-001', 'PE-002'],
        parentGate: 'GT-001',
        transferTo: null
      },
      {
        id: 'GT-003',
        description: 'Voting Gate - 2oo3',
        gateType: 'VOTING',
        kValue: 2,
        nValue: 3,
        inputs: ['PE-001', 'PE-002', 'PE-003'],
        parentGate: 'GT-001',
        transferTo: null
      }
    ],
    primaryevents: [
      {
        id: 'PE-001',
        description: 'Bearing Failure',
        eventType: 'Basic',
        probability: 0.05,
        failureModel: 'FM-001',
        commonCauseGroup: 'CCG-001',
        betaFactor: 0.05
      },
      {
        id: 'PE-002',
        description: 'Seal Leakage',
        eventType: 'Basic',
        probability: 0.08,
        failureModel: 'FM-002',
        commonCauseGroup: null,
        betaFactor: 0.02
      },
      {
        id: 'PE-003',
        description: 'Electrical Fault',
        eventType: 'Basic',
        probability: 0.02,
        failureModel: 'FM-004',
        commonCauseGroup: 'CCG-002',
        betaFactor: 0.08
      },
      {
        id: 'PE-004',
        description: 'Operator Error',
        eventType: 'House',
        probability: 0.01,
        failureModel: null,
        commonCauseGroup: null,
        betaFactor: null
      }
    ],
    phases: [
      {
        id: 'PHASE-001',
        description: 'Normal Operation',
        startTime: 0,
        endTime: 8760,
        systemConfig: 'All systems operational',
        operationalRules: 'Continuous 24/7 monitoring',
        failureRateMultiplier: 1.0
      },
      {
        id: 'PHASE-002',
        description: 'Maintenance Period',
        startTime: 8760,
        endTime: 9000,
        systemConfig: 'Partial system redundancy',
        operationalRules: 'Reduced throughput allowed',
        failureRateMultiplier: 1.2
      },
      {
        id: 'PHASE-003',
        description: 'Startup Phase',
        startTime: 0,
        endTime: 100,
        systemConfig: 'Ramping operation',
        operationalRules: 'Gradual load increase',
        failureRateMultiplier: 1.5
      }
    ],
    weibullsets: [
      {
        id: 'WB-001',
        description: 'Historical Bearing Failure Data',
        failureData: '45',
        suspensionData: '12',
        estimationMethod: 'MLE',
        beta: 1.82,
        eta: 8620,
        rSquared: 0.956,
        mttf: 7650
      },
      {
        id: 'WB-002',
        description: 'Seal Failure Analysis',
        failureData: '28',
        suspensionData: '8',
        estimationMethod: 'Rank Regression',
        beta: 1.05,
        eta: 5200,
        rSquared: 0.943,
        mttf: 5100
      }
    ],
    boms: [
      {
        id: 'BOM-001',
        description: 'Pump Assembly Complete',
        items: 12,
        partNumbers: 'PA-001, PA-002, PA-003...',
        quantities: '1,2,4...',
        costs: 5600,
        linkedSpares: 'SPR-001, SPR-002'
      },
      {
        id: 'BOM-002',
        description: 'Control Module Kit',
        items: 8,
        partNumbers: 'CM-001, CM-002, CM-003...',
        quantities: '1,2,1...',
        costs: 3200,
        linkedSpares: 'SPR-004'
      }
    ],
    products: [
      {
        id: 'PROD-001',
        description: 'Standard Product - Grade A',
        nominalCapacity: 1000,
        targetCapacity: 950,
        penaltyRate: 100,
        unit: 'units/hour'
      },
      {
        id: 'PROD-002',
        description: 'Premium Product - Grade B',
        nominalCapacity: 500,
        targetCapacity: 480,
        penaltyRate: 150,
        unit: 'units/hour'
      }
    ],
    rules: [
      {
        id: 'RULE-001',
        description: 'Maintenance Priority Escalation',
        ruleType: 'logic',
        triggerCondition: 'System Availability < 95%',
        action: 'Increase preventive maintenance frequency by 25%',
        targetEntity: 'All scheduled tasks'
      },
      {
        id: 'RULE-002',
        description: 'Spare Part Reorder Automation',
        ruleType: 'capacity',
        triggerCondition: 'Stock Level <= Reorder Point',
        action: 'Generate purchase order for reorderQuantity units',
        targetEntity: 'All spares'
      },
      {
        id: 'RULE-003',
        description: 'Standby Activation',
        ruleType: 'logic',
        triggerCondition: 'Primary Equipment fails',
        action: 'Activate standby equipment',
        targetEntity: 'EQP-002'
      },
      {
        id: 'RULE-004',
        description: 'Critical Threshold Alert',
        ruleType: 'capacity',
        triggerCondition: 'System Capacity < 80%',
        action: 'Send alert to operations team',
        targetEntity: 'All equipments'
      }
    ],
    costnodes: [
      {
        id: 'CN-001',
        description: 'Corrective Maintenance Labor',
        costCategory: 'Labor',
        annualCost: 125000,
        escalationRate: 3.5,
        parent: null
      },
      {
        id: 'CN-002',
        description: 'Preventive Maintenance Materials',
        costCategory: 'Materials',
        annualCost: 75000,
        escalationRate: 2.5,
        parent: null
      },
      {
        id: 'CN-003',
        description: 'Spare Parts Inventory',
        costCategory: 'Inventory',
        annualCost: 50000,
        escalationRate: 2.0,
        parent: 'CN-002'
      },
      {
        id: 'CN-004',
        description: 'Equipment Acquisition Costs',
        costCategory: 'Equipment',
        annualCost: 250000,
        escalationRate: 4.0,
        parent: null
      }
    ],
    rcmcauses: [
      {
        id: 'RCM-001',
        description: 'Inadequate lubrication',
        failureMode: 'Bearing excessive wear',
        effect: 'Loss of equipment function',
        severity: 8,
        occurrence: 6,
        detection: 4,
        rpn: 192,
        recommendedTask: 'Implement automated lubrication system',
        decisionPath: 'H-H-T'
      },
      {
        id: 'RCM-002',
        description: 'Corrosive fluid contact',
        failureMode: 'Seal degradation',
        effect: 'Product contamination',
        severity: 9,
        occurrence: 3,
        detection: 5,
        rpn: 135,
        recommendedTask: 'Increase inspection frequency',
        decisionPath: 'H-M-T'
      },
      {
        id: 'RCM-003',
        description: 'Thermal stress cycling',
        failureMode: 'Material fatigue cracking',
        effect: 'Equipment shutdown',
        severity: 7,
        occurrence: 5,
        detection: 3,
        rpn: 105,
        recommendedTask: 'Implement thermal management',
        decisionPath: 'H-M-T'
      },
      {
        id: 'RCM-004',
        description: 'Electrical overload',
        failureMode: 'Component burnout',
        effect: 'System failure',
        severity: 9,
        occurrence: 2,
        detection: 7,
        rpn: 126,
        recommendedTask: 'Install power conditioning system',
        decisionPath: 'H-L-T'
      }
    ],
    eventtrees: [
      {
        id: 'ET-001',
        description: 'Pump Failure - Consequence Analysis',
        initiatingEvent: 'PE-001',
        branches: 4,
        consequenceMapping: 'CONS-001, CONS-002'
      },
      {
        id: 'ET-002',
        description: 'Electrical Fault - Event Progression',
        initiatingEvent: 'PE-003',
        branches: 3,
        consequenceMapping: 'CONS-003'
      },
      {
        id: 'ET-003',
        description: 'Seal Failure Cascade',
        initiatingEvent: 'PE-002',
        branches: 5,
        consequenceMapping: 'CONS-002, CONS-004'
      },
      {
        id: 'ET-004',
        description: 'Fire Scenario Analysis',
        initiatingEvent: 'PE-004',
        branches: 6,
        consequenceMapping: 'CONS-003, CONS-004'
      }
    ]
  };
}

// ============================================================================
// CREATE BLANK PROJECT
// ============================================================================
function createBlankProject() {
  return {
    locations: [],
    equipments: [],
    failuremodels: [],
    consequences: [],
    spares: [],
    labor: [],
    equipmentresources: [],
    scheduledtasks: [],
    correctivetasks: [],
    taskgroups: [],
    rbdblocks: [],
    gates: [],
    primaryevents: [],
    phases: [],
    weibullsets: [],
    boms: [],
    products: [],
    rules: [],
    costnodes: [],
    rcmcauses: [],
    rcmFunctions: [],
    rcmFunctionalFailures: [],
    rcmStudies: [],
    eventtrees: [],
    predictionParts: [],
    fmecaItems: [],
    growthPhases: [],
    safetyHazards: [],
    allocationSubsystems: [],
    lifecycleAssessments: []
  };
}

// ============================================================================
// CUSTOM HOOK: UNDO/REDO SYSTEM
// ============================================================================
function useUndoRedo(initialState) {
  const [state, dispatch] = useReducer((currentState, action) => {
    switch (action.type) {
      case 'SET':
        return {
          ...currentState,
          past: [...currentState.past.slice(-49), currentState.present],
          present: action.payload,
          future: []
        };
      case 'SET_FN':
        return {
          ...currentState,
          past: [...currentState.past.slice(-49), currentState.present],
          present: action.updater(currentState.present),
          future: []
        };
      case 'UNDO':
        if (currentState.past.length === 0) return currentState;
        return {
          past: currentState.past.slice(0, -1),
          present: currentState.past[currentState.past.length - 1],
          future: [currentState.present, ...currentState.future]
        };
      case 'REDO':
        if (currentState.future.length === 0) return currentState;
        return {
          past: [...currentState.past, currentState.present],
          present: currentState.future[0],
          future: currentState.future.slice(1)
        };
      default:
        return currentState;
    }
  }, {
    past: [],
    present: initialState,
    future: []
  });

  const setState = useCallback((newState) => {
    if (typeof newState === 'function') {
      dispatch({ type: 'SET_FN', updater: newState });
    } else {
      dispatch({ type: 'SET', payload: newState });
    }
  }, []);

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, []);

  return [state.present, setState, undo, redo, state.past.length > 0, state.future.length > 0];
}

// ============================================================================
// MENU BAR COMPONENT
// ============================================================================
function MenuBar({
  onNew,
  onOpen,
  onSave,
  onExport,
  onUndo,
  onRedo,
  onModuleSelect,
  canUndo,
  canRedo,
  onImportExcel,
  onExportExcel,
  onImportXML,
  onBatchSimulation,
  onGenerateReport,
  onRunFTAnalysis,
  onToggleLeftPanel,
  onToggleRightPanel,
  onToggleGrid,
  onRunSimulation,
  onStopSimulation,
  onShowSimConfig,
  onShowAbout,
  onShowHelp,
  onFindReplace,
  onZoomIn,
  onZoomOut,
  onZoomFit,
  onSelectTab,
  currentModule
}) {
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems = {
    File: [
      { label: 'New', icon: File, onClick: onNew, shortcut: 'Ctrl+N' },
      { label: 'Open', icon: FolderOpen, onClick: onOpen, shortcut: 'Ctrl+O' },
      { label: 'Save', icon: Save, onClick: onSave, shortcut: 'Ctrl+S' },
      { label: 'Save As...', icon: Save, onClick: onSave, shortcut: 'Ctrl+Shift+S' },
      { divider: true },
      { label: 'Import CSV', onClick: () => {}, shortcut: 'Ctrl+Alt+I' },
      { label: 'Import Excel', onClick: onImportExcel, shortcut: 'Ctrl+Alt+E' },
      { label: 'Import XML (.oawx/.xml)', onClick: onImportXML, shortcut: 'Ctrl+Alt+M' },
      { divider: true },
      { label: 'Export CSV', onClick: onExport, shortcut: 'Ctrl+Alt+C' },
      { label: 'Export Excel', onClick: onExportExcel, shortcut: 'Ctrl+Alt+X' },
      { label: 'Export XML (.oawx)', onClick: onExport, shortcut: 'Ctrl+Alt+O' },
      { label: 'Export PDF', onClick: onExport, shortcut: 'Ctrl+Alt+P' },
      { divider: true },
      { label: 'Exit', onClick: () => window.close(), shortcut: 'Alt+F4' }
    ],
    Edit: [
      { label: 'Undo', icon: RotateCcw, onClick: onUndo, shortcut: 'Ctrl+Z', disabled: !canUndo },
      { label: 'Redo', icon: RotateCw, onClick: onRedo, shortcut: 'Ctrl+Y', disabled: !canRedo },
      { divider: true },
      { label: 'Cut', onClick: () => {}, shortcut: 'Ctrl+X' },
      { label: 'Copy', icon: Copy, onClick: () => {}, shortcut: 'Ctrl+C' },
      { label: 'Paste', onClick: () => {}, shortcut: 'Ctrl+V' },
      { label: 'Delete', icon: Trash2, onClick: () => {}, shortcut: 'Delete' },
      { divider: true },
      { label: 'Select All', onClick: () => {}, shortcut: 'Ctrl+A' },
      { label: 'Find/Replace', onClick: onFindReplace, shortcut: 'Ctrl+H' }
    ],
    View: [
      { label: 'Toggle Left Panel', onClick: onToggleLeftPanel, shortcut: 'Ctrl+1' },
      { label: 'Toggle Right Panel', onClick: onToggleRightPanel, shortcut: 'Ctrl+2' },
      { label: 'Show Grid', onClick: onToggleGrid, shortcut: 'Ctrl+G' },
      { label: 'Show RBD Arrows', onClick: () => onSelectTab && onSelectTab('rbd'), shortcut: 'Ctrl+R' },
      { label: 'Show FT IEC Symbols', onClick: () => onSelectTab && onSelectTab('ft'), shortcut: 'Ctrl+I' },
      { divider: true },
      { label: 'Zoom In', icon: ZoomIn, onClick: onZoomIn, shortcut: 'Ctrl+=' },
      { label: 'Zoom Out', icon: ZoomOut, onClick: onZoomOut, shortcut: 'Ctrl+-' },
      { label: 'Zoom Fit', icon: Maximize2, onClick: onZoomFit, shortcut: 'Ctrl+0' }
    ],
    Modules: [
      { label: `${currentModule === 'hub' ? '●' : '○'} Module Hub`, onClick: () => onModuleSelect('hub'), radio: true },
      { label: `${currentModule === 'outcomes' ? '●' : '○'} Outcomes Hub`, onClick: () => onModuleSelect('outcomes'), radio: true },
      { divider: true },
      { label: `${currentModule === 'optimalav' ? '●' : '○'} OptimalAV (Availability)`, onClick: () => onModuleSelect('optimalav'), radio: true },
      { label: `${currentModule === 'rcm' ? '●' : '○'} OptimalRCM`, onClick: () => onModuleSelect('rcm'), radio: true },
      { label: `${currentModule === 'weibull' ? '●' : '○'} Weibull Analysis`, onClick: () => onModuleSelect('weibull'), radio: true },
      { label: `${currentModule === 'alt' ? '●' : '○'} ALT (Accelerated Life)`, onClick: () => onModuleSelect('alt'), radio: true },
      { label: `${currentModule === 'lcc' ? '●' : '○'} Life Cycle Cost`, onClick: () => onModuleSelect('lcc'), radio: true },
      { label: `${currentModule === 'process' ? '●' : '○'} Process Reliability / OEE`, onClick: () => onModuleSelect('process'), radio: true },
      { label: `${currentModule === 'reliability' ? '●' : '○'} Reliability Engineering`, onClick: () => onModuleSelect('reliability'), radio: true },
      { divider: true },
      { label: `${currentModule === 'enterprise' ? '●' : '○'} Enterprise Manager`, onClick: () => onModuleSelect('enterprise'), radio: true }
    ],
    Simulation: [
      { label: 'Run Simulation', icon: Play, onClick: onRunSimulation, shortcut: 'F5' },
      { label: 'Stop Simulation', icon: Square, onClick: onStopSimulation, shortcut: 'Escape' },
      { label: 'Configure Parameters...', icon: Settings, onClick: onShowSimConfig, shortcut: 'F6' },
      { divider: true },
      { label: 'Run Fault Tree Analysis', onClick: onRunFTAnalysis, shortcut: 'Ctrl+F5' },
      { divider: true },
      { label: 'Batch Simulation', onClick: onBatchSimulation, shortcut: 'Ctrl+B' }
    ],
    Tools: [
      { label: 'Validate Data', onClick: () => { alert('Data validation: All entities valid.'); }, shortcut: 'F7' },
      { label: 'Spares Analysis', onClick: () => onSelectTab && onSelectTab('spares'), shortcut: 'F8' },
      { divider: true },
      { label: 'SAP CMMS Connector', onClick: () => onSelectTab && onSelectTab('sap'), shortcut: 'Ctrl+Shift+S' },
      { label: 'IBM Maximo Connector', onClick: () => onSelectTab && onSelectTab('maximo'), shortcut: 'Ctrl+Shift+M' },
      { divider: true },
      { label: 'Interval Optimization', onClick: () => onSelectTab && onSelectTab('optimization'), shortcut: 'F9' },
      { label: 'Work Instruction Generator', onClick: () => onSelectTab && onSelectTab('workinstructions'), shortcut: 'Ctrl+Shift+W' },
      { label: 'Component Libraries', onClick: () => onSelectTab && onSelectTab('library'), shortcut: 'Ctrl+Shift+L' },
      { divider: true },
      { label: 'Options/Preferences', icon: Settings, onClick: onShowSimConfig, shortcut: 'Ctrl+,' }
    ],
    Reports: [
      { label: 'Standard Reports', onClick: onGenerateReport, shortcut: 'Ctrl+R' },
      { label: 'Custom Report', onClick: onGenerateReport, shortcut: 'Ctrl+Shift+R' },
      { divider: true },
      { label: 'Export Report PDF', icon: Download, onClick: onGenerateReport, shortcut: 'Ctrl+Shift+P' }
    ],
    Help: [
      { label: 'Help Topics', icon: HelpCircle, onClick: onShowHelp, shortcut: 'F1' },
      { label: 'Getting Started Guide', icon: BookOpen, onClick: onShowHelp, shortcut: null },
      { label: 'Keyboard Shortcuts', onClick: onShowHelp, shortcut: 'Ctrl+/' },
      { divider: true },
      { label: 'Optimal® Website', onClick: () => window.open('https://optimal.world', '_blank'), shortcut: null },
      { label: 'ARaaS® Documentation', onClick: () => window.open('https://optimal.world', '_blank'), shortcut: null },
      { divider: true },
      { label: 'About Optimal Availability Studio', onClick: onShowAbout, shortcut: null },
      { label: 'License Information', onClick: () => {}, shortcut: null }
    ]
  };

  // Official Optimal® logo — real PNG, not SVG approximation
  const LB = '#00A8CB'; // Light Blue
  const DB = '#002F65'; // Dark Blue
  const OPTIMAL_LOGO_SRC = window.__OPTIMAL_LOGO_COLOUR_B64 || '/optimal-logo-colour.png';
  const OptimalLogo = () => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '0 10px 0 6px', gap: '0', cursor: 'pointer', userSelect: 'none' }} onClick={() => onModuleSelect && onModuleSelect('hub')} title="Back to Module Hub">
      <img src={OPTIMAL_LOGO_SRC} alt="Optimal®" style={{ height: '26px', width: 'auto', marginRight: '2px' }} />
      <sup style={{ fontSize: '9px', fontWeight: '700', color: LB, marginLeft: '1px', lineHeight: 1 }}>
        {currentModule === 'rcm' || currentModule === 'optimalrcm' ? 'RCM' : 'AV'}
      </sup>
      <div style={{ width: '1px', height: '20px', backgroundColor: COLORS.BORDER, marginLeft: '10px' }}></div>
      {/* ARaaS® badge */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: DB,
        borderRadius: '2px',
        padding: '2px 6px',
        marginLeft: '4px',
        marginRight: '6px'
      }}>
        <span style={{ fontSize: '8px', fontWeight: '700', color: LB, letterSpacing: '0.5px' }}>ARaaS</span>
        <span style={{ fontSize: '6px', fontWeight: '700', color: LB, position: 'relative', top: '-3px', marginLeft: '0.5px' }}>®</span>
      </div>
    </div>
  );

  return (
    <div ref={menuRef} style={{ display: 'flex', gap: '1px', backgroundColor: COLORS.PANEL_BG, alignItems: 'center', borderBottom: `2px solid ${COLORS.NAVY}` }}>
      <OptimalLogo />
      {/* Menu items */}
      {Object.entries(menuItems).map(([menuName, items]) => (
        <div key={menuName} style={{ position: 'relative' }}>
          <button
            onClick={() => setOpenMenu(openMenu === menuName ? null : menuName)}
            style={{
              backgroundColor: openMenu === menuName ? COLORS.TEAL : 'transparent',
              color: openMenu === menuName ? '#FFFFFF' : COLORS.TEXT,
              border: 'none',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              borderRadius: '3px 3px 0 0',
              transition: 'background-color 0.15s'
            }}
          >
            {menuName}
          </button>

          {openMenu === menuName && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                backgroundColor: COLORS.PANEL_BG,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                minWidth: '220px',
                zIndex: 1000,
                boxShadow: '0 4px 16px rgba(5,28,44,0.12), 0 1px 3px rgba(5,28,44,0.08)'
              }}
            >
              {items.map((item, idx) =>
                item.divider ? (
                  <div
                    key={idx}
                    style={{
                      height: '1px',
                      backgroundColor: COLORS.BORDER,
                      margin: '4px 0'
                    }}
                  />
                ) : (
                  <button
                    key={idx}
                    onClick={() => {
                      item.onClick?.();
                      setOpenMenu(null);
                    }}
                    disabled={item.disabled}
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent',
                      color: item.disabled ? COLORS.TEXT_DIM : COLORS.TEXT,
                      border: 'none',
                      padding: '8px 12px',
                      textAlign: 'left',
                      cursor: item.disabled ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: item.disabled ? 0.5 : 1,
                      transition: 'background-color 0.1s'
                    }}
                    onMouseEnter={(e) => {
                      if (!item.disabled) {
                        e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
                        e.target.style.color = COLORS.TEAL;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = item.disabled ? COLORS.TEXT_DIM : COLORS.TEXT;
                    }}
                  >
                    {item.icon && <item.icon size={16} />}
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.shortcut && (
                      <span style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginLeft: '8px' }}>
                        {item.shortcut}
                      </span>
                    )}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      ))}

      {/* Right side — user info + logout */}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 12px' }}>
        {typeof window !== 'undefined' && window.oavUser && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '24px', height: '24px', borderRadius: '50%',
                backgroundColor: COLORS.NAVY, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: '700'
              }}>
                {(window.oavUser().fullName || window.oavUser().username || 'U').charAt(0).toUpperCase()}
              </div>
              <div style={{ lineHeight: '1.2' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: COLORS.TEXT }}>
                  {window.oavUser().fullName || window.oavUser().username || 'User'}
                </div>
                <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {window.oavUser().role || 'analyst'}
                </div>
              </div>
            </div>
            <button
              onClick={() => { if (window.oavLogout) window.oavLogout(); }}
              style={{
                backgroundColor: 'transparent',
                color: COLORS.TEXT_DIM,
                border: `1px solid ${COLORS.BORDER}`,
                padding: '4px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '500',
                transition: 'all 0.15s'
              }}
              title="Sign out"
            >
              Sign Out
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// TOOLBAR COMPONENT
// ============================================================================
function Toolbar({ onNew, onOpen, onSave, onUndo, onRedo, onAddItem, onDeleteItem, onRun, onStop, onZoom, canUndo, canRedo, isProcessing }) {
  const toolbarStyle = {
    display: 'flex',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: COLORS.PANEL_BG,
    borderBottom: `1px solid ${COLORS.BORDER}`,
    alignItems: 'center',
    flexWrap: 'wrap'
  };

  const buttonGroupStyle = {
    display: 'flex',
    gap: '4px',
    padding: '0 8px',
    borderRight: `1px solid ${COLORS.BORDER}`
  };

  const buttonStyle = (disabled = false) => ({
    backgroundColor: 'transparent',
    color: COLORS.TEXT,
    border: `1px solid ${COLORS.BORDER}`,
    padding: '5px 10px',
    borderRadius: '3px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    opacity: disabled ? 0.4 : 1,
    transition: 'all 0.15s ease',
    fontWeight: '400',
  });

  const ToolButton = ({ icon: Icon, label, onClick, disabled, title }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={buttonStyle(disabled)}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.backgroundColor = COLORS.TEAL_LIGHT; e.currentTarget.style.borderColor = COLORS.TEAL; e.currentTarget.style.color = COLORS.TEAL; }}}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = COLORS.BORDER; e.currentTarget.style.color = COLORS.TEXT; }}
    >
      <Icon size={16} />
      {label && <span style={{ marginLeft: '4px' }}>{label}</span>}
    </button>
  );

  return (
    <div style={toolbarStyle}>
      {/* File Operations */}
      <div style={buttonGroupStyle}>
        <ToolButton icon={File} label="New" onClick={onNew} title="Create new project (Ctrl+N)" />
        <ToolButton icon={FolderOpen} label="Open" onClick={onOpen} title="Open project (Ctrl+O)" />
        <ToolButton icon={Save} label="Save" onClick={onSave} title="Save project (Ctrl+S)" />
      </div>

      {/* Edit Operations */}
      <div style={buttonGroupStyle}>
        <ToolButton icon={RotateCcw} onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)" />
        <ToolButton icon={RotateCw} onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Y)" />
      </div>

      {/* Entity Operations */}
      <div style={buttonGroupStyle}>
        <ToolButton icon={Plus} label="Add" onClick={onAddItem} title="Add new entity" />
        <ToolButton icon={Trash2} onClick={onDeleteItem} title="Delete selected entity" />
        <ToolButton icon={Copy} onClick={() => {}} title="Duplicate entity (Ctrl+D)" />
      </div>

      {/* Simulation Operations */}
      <div style={buttonGroupStyle}>
        <ToolButton icon={Play} label={isProcessing ? "Running..." : "Run"} onClick={onRun} disabled={isProcessing} title={isProcessing ? "Processing in progress..." : "Run simulation (F5)"} />
        <ToolButton icon={Square} label="Stop" onClick={onStop} disabled={!isProcessing} title="Stop simulation (Shift+F5)" />
        <ToolButton icon={Settings} onClick={() => {}} title="Configure (F6)" />
      </div>

      {/* View Operations */}
      <div style={buttonGroupStyle}>
        <ToolButton icon={ZoomIn} onClick={() => onZoom(1.2)} title="Zoom in (Ctrl+=)" />
        <ToolButton icon={ZoomOut} onClick={() => onZoom(0.8)} title="Zoom out (Ctrl+-)" />
        <ToolButton icon={Maximize2} onClick={() => onZoom('fit')} title="Zoom fit (Ctrl+0)" />
      </div>

      {/* Filter Toggle */}
      <div style={buttonGroupStyle}>
        <ToolButton icon={Filter} onClick={() => {}} title="Toggle filter (Ctrl+F)" />
      </div>
    </div>
  );
}

// ============================================================================
// TREE SECTION COMPONENT (LEFT PANEL)
// ============================================================================
function TreeSection({
  section,
  items,
  selectedItem,
  onSelectItem,
  onAddItem,
  onDeleteItem,
  onReparent,
  onEditItem
}) {
  const [expanded, setExpanded] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextItem, setContextItem] = useState(null);

  const filteredItems = (items || []).filter(item =>
    item.description?.toLowerCase().includes(searchText.toLowerCase()) ||
    item.id.toLowerCase().includes(searchText.toLowerCase())
  );

  const hierarchyItems = useMemo(() => {
    if (section.id !== 'locations') return filteredItems;
    // Build tree order: roots first, then depth-first children
    const byParent = {};
    filteredItems.forEach(item => {
      const pid = item.parent || '__root__';
      if (!byParent[pid]) byParent[pid] = [];
      byParent[pid].push(item);
    });
    const ordered = [];
    const walk = (parentId) => {
      const children = byParent[parentId] || [];
      children.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
      children.forEach(child => {
        ordered.push(child);
        walk(child.id);
      });
    };
    walk('__root__');
    // Also add any items whose parent doesn't exist (treat as root)
    filteredItems.forEach(item => {
      if (!ordered.includes(item)) ordered.push(item);
    });
    return ordered;
  }, [filteredItems, section.id]);

  const getIndentation = (item) => {
    if (section.id === 'locations') {
      return (item.hierarchyLevel || 0) * 20;
    }
    return 0;
  };

  return (
    <div
      style={{
        borderBottom: `1px solid ${COLORS.BORDER}`,
        marginBottom: '12px'
      }}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setContextMenu({ x: e.clientX, y: e.clientY });
          setContextItem(null); // null indicates header context
        }}
        style={{
          width: '100%',
          backgroundColor: COLORS.PANEL_BG,
          color: COLORS.TEXT,
          border: 'none',
          padding: '10px 8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          fontWeight: '600',
          borderRadius: '4px',
          marginBottom: '4px'
        }}
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <section.icon size={16} style={{ color: section.color }} />
        {section.label}
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: COLORS.TEXT_DIM }}>
          ({filteredItems.length})
        </span>
      </button>

      {expanded && (
        <div style={{ paddingLeft: '8px' }}>
          {/* Search Box */}
          <input
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              width: '100%',
              backgroundColor: COLORS.LIGHT_BG,
              color: COLORS.TEXT,
              border: `1px solid ${COLORS.BORDER}`,
              padding: '6px 8px',
              borderRadius: '3px',
              fontSize: '12px',
              marginBottom: '8px',
              boxSizing: 'border-box'
            }}
          />

          {/* Items */}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {hierarchyItems.length === 0 ? (
              <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', padding: '8px' }}>
                No items
              </div>
            ) : (
              hierarchyItems.map(item => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 8px',
                    marginBottom: '4px',
                    backgroundColor: selectedItem?.id === item.id ? COLORS.NAVY : 'transparent',
                    color: selectedItem?.id === item.id ? '#FFFFFF' : COLORS.TEXT,
                    borderRadius: '3px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    marginLeft: `${getIndentation(item)}px`,
                    fontSize: '13px'
                  }}
                  onClick={() => onSelectItem(item)}
                  onDoubleClick={() => setEditingId(item.id)}
                  onMouseEnter={(e) => {
                    if (selectedItem?.id !== item.id) {
                      e.currentTarget.style.backgroundColor = COLORS.BORDER;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedItem?.id !== item.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <span style={{ minWidth: '60px', fontSize: '11px', color: selectedItem?.id === item.id ? '#FFFFFFBB' : COLORS.TEXT_DIM }}>
                    {item.id}
                  </span>
                  <span style={{ flex: 1, color: selectedItem?.id === item.id ? '#FFFFFF' : COLORS.TEXT }}>
                    {editingId === item.id ? (
                      <input
                        autoFocus
                        defaultValue={item.description}
                        onBlur={() => setEditingId(null)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            setEditingId(null);
                          }
                        }}
                        style={{
                          backgroundColor: COLORS.LIGHT_BG,
                          color: COLORS.TEXT,
                          border: `1px solid ${COLORS.GOLD}`,
                          padding: '2px 4px',
                          fontSize: '13px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      item.description
                    )}
                  </span>
                  <MoreVertical
                    size={14}
                    onClick={(e) => {
                      e.stopPropagation();
                      setContextMenu({ x: e.clientX, y: e.clientY });
                      setContextItem(item);
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setContextMenu({ x: e.clientX, y: e.clientY });
                      setContextItem(item);
                    }}
                    style={{ cursor: 'pointer', opacity: 0.6 }}
                  />
                </div>
              ))
            )}
          </div>

          {/* Add Button */}
          <button
            onClick={() => onAddItem(section.id)}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              color: COLORS.GOLD,
              border: `1px dashed ${COLORS.BORDER}`,
              padding: '6px 8px',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '12px',
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              justifyContent: 'center'
            }}
          >
            <Plus size={14} /> Add {section.label}
          </button>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <>
          {/* Close on click outside */}
          <div
            onClick={() => {
              setContextMenu(null);
              setContextItem(null);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
          />
          {/* Menu Items */}
          <div
            style={{
              position: 'fixed',
              top: contextMenu.y,
              left: contextMenu.x,
              backgroundColor: COLORS.PANEL_BG,
              border: `1px solid ${COLORS.BORDER}`,
              borderRadius: '4px',
              minWidth: '120px',
              zIndex: 1000,
              boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
            }}
          >
            {contextItem === null ? (
              // Header context menu
              <>
                <button
                  onClick={() => {
                    onAddItem(section.id);
                    setContextMenu(null);
                    setContextItem(null);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: COLORS.TEXT,
                    border: 'none',
                    padding: '8px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background-color 0.1s'
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = COLORS.BORDER)}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                >
                  Add New
                </button>
                <div style={{ height: '1px', backgroundColor: COLORS.BORDER, margin: '2px 0' }} />
                <button
                  onClick={() => {
                    setExpanded(false);
                    setContextMenu(null);
                    setContextItem(null);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: COLORS.TEXT,
                    border: 'none',
                    padding: '8px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background-color 0.1s'
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = COLORS.BORDER)}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                >
                  Collapse All
                </button>
                <button
                  onClick={() => {
                    setExpanded(true);
                    setContextMenu(null);
                    setContextItem(null);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: COLORS.TEXT,
                    border: 'none',
                    padding: '8px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background-color 0.1s'
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = COLORS.BORDER)}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                >
                  Expand All
                </button>
              </>
            ) : (
              // Item context menu
              <>
                <button
                  onClick={() => {
                    onSelectItem(contextItem);
                    if (onEditItem) onEditItem(contextItem);
                    setContextMenu(null);
                    setContextItem(null);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: COLORS.TEXT,
                    border: 'none',
                    padding: '8px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background-color 0.1s'
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = COLORS.BORDER)}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                >
                  Edit
                </button>
                <div style={{ height: '1px', backgroundColor: COLORS.BORDER, margin: '2px 0' }} />
                <button
                  onClick={() => {
                    onDeleteItem(contextItem.id);
                    setContextMenu(null);
                    setContextItem(null);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: COLORS.TEXT,
                    border: 'none',
                    padding: '8px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background-color 0.1s'
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = COLORS.BORDER)}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                >
                  Delete
                </button>
                <div style={{ height: '1px', backgroundColor: COLORS.BORDER, margin: '2px 0' }} />
                <button
                  onClick={() => {
                    // Duplicate: add similar item with new ID
                    const newItem = { ...contextItem, id: `${contextItem.id}-COPY-${Date.now().toString().slice(-6)}`, description: `${contextItem.description} (Copy)` };
                    onAddItem(section.id);
                    setContextMenu(null);
                    setContextItem(null);
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: COLORS.TEXT,
                    border: 'none',
                    padding: '8px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background-color 0.1s'
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = COLORS.BORDER)}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                >
                  Duplicate
                </button>
                {section.id === 'locations' && (
                  <>
                    <div style={{ height: '1px', backgroundColor: COLORS.BORDER, margin: '2px 0' }} />
                    <button
                      onClick={() => {
                        onAddItem(section.id);
                        setContextMenu(null);
                        setContextItem(null);
                      }}
                      style={{
                        width: '100%',
                        backgroundColor: 'transparent',
                        color: COLORS.TEXT,
                        border: 'none',
                        padding: '8px 12px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background-color 0.1s'
                      }}
                      onMouseEnter={(e) => (e.target.style.backgroundColor = COLORS.BORDER)}
                      onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                    >
                      Add Child
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// ENHANCED DATA GRID COMPONENT
// ============================================================================
function DataGrid({
  data,
  columns,
  selectedRows,
  onSelectRows,
  onEditCell,
  onAddRow,
  onDeleteRow,
  onRowClick
}) {
  const [sortColumn, setSortColumn] = useState(columns[0].key);
  const [sortOrder, setSortOrder] = useState('asc');
  const [editingCell, setEditingCell] = useState(null);
  const [filterValues, setFilterValues] = useState({});

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, sortColumn, sortOrder]);

  const handleSort = (key) => {
    if (sortColumn === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(key);
      setSortOrder('asc');
    }
  };

  const handleSelectRow = (id, ctrlKey, shiftKey) => {
    let newSelection = [...selectedRows];
    if (shiftKey && selectedRows.length > 0) {
      // Range selection
      const startIdx = data.findIndex(r => r.id === selectedRows[selectedRows.length - 1]);
      const endIdx = data.findIndex(r => r.id === id);
      const start = Math.min(startIdx, endIdx);
      const end = Math.max(startIdx, endIdx);
      newSelection = data.slice(start, end + 1).map(r => r.id);
    } else if (ctrlKey) {
      if (newSelection.includes(id)) {
        newSelection = newSelection.filter(s => s !== id);
      } else {
        newSelection.push(id);
      }
    } else {
      newSelection = [id];
    }
    onSelectRows(newSelection);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ overflowX: 'auto', flex: 1 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px'
          }}
        >
          {/* Header with Filters */}
          <thead>
            <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <th style={{ width: '40px', padding: '6px' }}>
                <input
                  type="checkbox"
                  checked={selectedRows.length === data.length && data.length > 0}
                  onChange={(e) => {
                    onSelectRows(e.target.checked ? data.map(r => r.id) : []);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{
                    width: `${col.width}px`,
                    padding: '8px',
                    textAlign: 'left',
                    backgroundColor: COLORS.PANEL_BG,
                    color: COLORS.TEXT,
                    fontWeight: '600',
                    borderRight: `1px solid ${COLORS.BORDER}`,
                    cursor: 'pointer',
                    userSelect: 'none',
                    position: 'relative'
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {col.label}
                    {sortColumn === col.key && (
                      <span style={{ fontSize: '10px' }}>
                        {sortOrder === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
            {/* Filter Row */}
            <tr style={{ backgroundColor: COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <th style={{ width: '40px' }}></th>
              {columns.map(col => (
                <th key={`filter-${col.key}`} style={{ width: `${col.width}px`, padding: '4px' }}>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={filterValues[col.key] || ''}
                    onChange={(e) => {
                      setFilterValues({ ...filterValues, [col.key]: e.target.value });
                    }}
                    style={{
                      width: '100%',
                      backgroundColor: COLORS.PANEL_BG,
                      color: COLORS.TEXT,
                      border: `1px solid ${COLORS.BORDER}`,
                      padding: '4px',
                      fontSize: '11px',
                      boxSizing: 'border-box',
                      borderRadius: '2px'
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {sortedData.map((row, idx) => (
              <tr
                key={row.id}
                onClick={(e) => {
                  if (e.target.tagName === 'INPUT') return;
                  handleSelectRow(row.id, e.ctrlKey, e.shiftKey);
                  if (onRowClick) onRowClick(row);
                }}
                style={{
                  backgroundColor: selectedRows.includes(row.id) ? COLORS.TEAL_LIGHT : (idx % 2 === 0 ? COLORS.PANEL_BG : COLORS.LIGHT_BG),
                  borderBottom: `1px solid ${COLORS.BORDER}`,
                  transition: 'background-color 0.1s',
                  cursor: 'pointer'
                }}
              >
                <td style={{ width: '40px', padding: '6px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleSelectRow(row.id, false, false)}
                    onClick={(e) => {
                      handleSelectRow(row.id, e.ctrlKey, e.shiftKey);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </td>
                {columns.map(col => (
                  <td
                    key={`${row.id}-${col.key}`}
                    style={{
                      width: `${col.width}px`,
                      padding: '6px 8px',
                      color: COLORS.TEXT,
                      borderRight: `1px solid ${COLORS.BORDER}`,
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: col.editable !== false ? 'text' : 'default'
                    }}
                    onDoubleClick={() => {
                      if (col.editable !== false) {
                        setEditingCell({ rowId: row.id, colKey: col.key });
                      }
                    }}
                    title={row[col.key]}
                  >
                    {editingCell?.rowId === row.id && editingCell?.colKey === col.key ? (
                      <input
                        autoFocus
                        type={col.type === 'number' ? 'number' : 'text'}
                        defaultValue={row[col.key]}
                        onBlur={(e) => {
                          onEditCell(row.id, col.key, e.target.value);
                          setEditingCell(null);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            onEditCell(row.id, col.key, e.target.value);
                            setEditingCell(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          backgroundColor: COLORS.LIGHT_BG,
                          color: COLORS.TEXT,
                          border: `1px solid ${COLORS.GOLD}`,
                          padding: '4px',
                          fontSize: '12px',
                          borderRadius: '2px'
                        }}
                      />
                    ) : (
                      <span>
                        {col.type === 'checkbox' ? (row[col.key] ? '✓' : '✗') : row[col.key]}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: `1px solid ${COLORS.BORDER}`,
          padding: '8px 12px',
          backgroundColor: COLORS.PANEL_BG,
          fontSize: '12px',
          color: COLORS.TEXT_DIM,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span>{selectedRows.length} of {sortedData.length} rows selected</span>
        <button
          onClick={() => onAddRow(columns[0].key)}
          style={{
            backgroundColor: COLORS.NAVY,
            color: '#FFFFFF',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <Plus size={12} /> Add Row
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// PROPERTY INSPECTOR COMPONENT
// ============================================================================
function PropertyInspector({ selectedItem, entityType, onUpdate, allData }) {
  const columns = COLUMN_DEFINITIONS[entityType] || [];
  const groupedFields = useMemo(() => {
    const groups = {
      'Basic': [],
      'Parameters': [],
      'Resources': [],
      'Costs': [],
      'Results': [],
      'Advanced': []
    };

    columns.forEach(col => {
      if (['id', 'description'].includes(col.key)) {
        groups['Basic'].push(col);
      } else if (['distribution', 'eta', 'beta', 'gamma', 'mttf', 'lambda', 'mu', 'sigma'].includes(col.key)) {
        groups['Parameters'].push(col);
      } else if (['laborReq', 'spareReq', 'taskReq', 'available', 'crewSize'].includes(col.key)) {
        groups['Resources'].push(col);
      } else if (['cost', 'costRate', 'costPerOccurrence', 'unitCost', 'annualCost'].includes(col.key)) {
        groups['Costs'].push(col);
      } else if (['mttf', 'mttr', 'rSquared', 'state'].includes(col.key)) {
        groups['Results'].push(col);
      } else {
        groups['Advanced'].push(col);
      }
    });

    return Object.fromEntries(
      Object.entries(groups).filter(([_, cols]) => cols.length > 0)
    );
  }, [columns]);

  if (!selectedItem || !entityType) {
    return (
      <div style={{ padding: '16px', color: COLORS.TEXT_DIM }}>
        Select an item to view properties
      </div>
    );
  }

  const renderFieldInput = (col) => {
    const value = selectedItem[col.key] || '';

    if (col.editable === false) {
      return (
        <input
          type="text"
          value={value}
          disabled
          style={{
            width: '100%',
            backgroundColor: COLORS.LIGHT_BG,
            color: COLORS.TEXT_DIM,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '6px 8px',
            borderRadius: '3px',
            fontSize: '12px',
            cursor: 'not-allowed'
          }}
        />
      );
    }

    switch (col.type) {
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => onUpdate({ ...selectedItem, [col.key]: parseFloat(e.target.value) })}
            style={{
              width: '100%',
              backgroundColor: COLORS.PANEL_BG,
              color: COLORS.TEXT,
              border: `1px solid ${COLORS.BORDER}`,
              padding: '6px 8px',
              borderRadius: '3px',
              fontSize: '12px'
            }}
          />
        );
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => onUpdate({ ...selectedItem, [col.key]: e.target.checked })}
            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
          />
        );
      case 'select':
        const getSelectOptions = () => {
          const options = col.options || [];
          if (options.length > 0) return options;

          // Generate options based on field and available data
          const optionMap = {
            'parent': allData?.locations?.map(l => l.id) || [],
            'phaseAssignment': allData?.phases?.map(p => p.id) || [],
            'location': allData?.locations?.map(l => l.id) || [],
            'redundancyType': ['series', 'parallel', 'kofn'],
            'type': ['Centrifugal Pump', 'Rotary Compressor', 'Programmable Logic Controller', 'Filtration Unit'],
            'distribution': ['Exponential', 'Weibull', 'Lognormal', 'Fixed', 'BiWeibull'],
            'taskType': ['inspection', 'replacement', 'overhaul', 'repair'],
            'gateType': ['OR', 'AND', 'VOTING', 'XOR'],
            'blockType': ['series', 'parallel', 'kofn'],
            'eventType': ['Basic', 'House'],
            'state': ['operating', 'standby', 'failed', 'maintenance'],
            'failureModel': allData?.failuremodels?.map(fm => fm.id) || [],
            'poolType': ['pooled', 'dedicated'],
            'ruleType': ['logic', 'capacity', 'scheduling'],
            'costCategory': ['Labor', 'Materials', 'Inventory', 'Equipment'],
            'initiatingEvent': allData?.primaryevents?.map(pe => pe.id) || [],
            'repairDistribution': ['Fixed', 'LogNormal', 'Exponential'],
            'laborReq': allData?.labor?.map(l => l.id) || [],
            'spareReq': allData?.spares?.map(s => s.id) || []
          };
          return optionMap[col.key] || [];
        };

        const selectOptions = getSelectOptions();
        return (
          <select
            value={value}
            onChange={(e) => onUpdate({ ...selectedItem, [col.key]: e.target.value })}
            style={{
              width: '100%',
              backgroundColor: COLORS.PANEL_BG,
              color: COLORS.TEXT,
              border: `1px solid ${COLORS.BORDER}`,
              padding: '6px 8px',
              borderRadius: '3px',
              fontSize: '12px'
            }}
          >
            <option value="">-- Select --</option>
            {selectOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onUpdate({ ...selectedItem, [col.key]: e.target.value })}
            style={{
              width: '100%',
              backgroundColor: COLORS.PANEL_BG,
              color: COLORS.TEXT,
              border: `1px solid ${COLORS.BORDER}`,
              padding: '6px 8px',
              borderRadius: '3px',
              fontSize: '12px'
            }}
          />
        );
    }
  };

  return (
    <div style={{ overflowY: 'auto', height: '100%', padding: '12px' }}>
      <h3 style={{ color: COLORS.GOLD, marginBottom: '16px', fontSize: '14px' }}>
        {selectedItem.id} - {entityType}
      </h3>

      {Object.entries(groupedFields).map(([groupName, fields]) => (
        <fieldset
          key={groupName}
          style={{
            border: `1px solid ${COLORS.BORDER}`,
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '12px'
          }}
        >
          <legend style={{ color: COLORS.GOLD, fontSize: '12px', fontWeight: '600' }}>
            {groupName}
          </legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {fields.map(col => (
              <div key={col.key}>
                <label style={{ display: 'block', fontSize: '11px', color: COLORS.TEXT, marginBottom: '4px', fontWeight: '500' }}>
                  {col.label}
                </label>
                {renderFieldInput(col)}
              </div>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

// ============================================================================
// TOP NAV COMPONENT - Hybrid horizontal navigation (replaces sidebar)
// ============================================================================
function TopNav({ currentModule, onModuleSelect, onShowHelp }) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Module structure with grouping for dropdowns
  const moduleGroups = {
    'Module Hub': { id: 'hub', modules: [] },
    'Outcomes': { id: 'outcomes', modules: [] },
    'FMEA/RCM': {
      id: 'fmea-group',
      modules: [
        { id: 'optimalav', label: 'Availability Analysis' },
        { id: 'rcm', label: 'RCM' },
        { id: 'weibull', label: 'Weibull Analysis' }
      ]
    },
    'Maintenance': {
      id: 'maintenance-group',
      modules: [
        { id: 'alt', label: 'ALT (Accelerated Life)' },
        { id: 'lcc', label: 'Life Cycle Cost' }
      ]
    },
    'Reliability': {
      id: 'reliability-group',
      modules: [
        { id: 'process', label: 'Process Reliability / OEE' },
        { id: 'reliability', label: 'Reliability Engineering' }
      ]
    },
    'Admin': { id: 'admin', modules: [
      { id: 'enterprise', label: 'Enterprise Manager' }
    ] }
  };

  const LB = '#00A8CB';
  const DB = '#002F65';
  const OPTIMAL_LOGO_SRC = window.__OPTIMAL_LOGO_COLOUR_B64 || '/optimal-logo-colour.png';

  const navLinkStyle = (isActive, isDropdown = false) => ({
    backgroundColor: 'transparent',
    color: isActive ? LB : COLORS.NAVY,
    border: 'none',
    padding: '0 12px',
    height: '48px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    borderBottom: isActive ? `3px solid ${LB}` : '3px solid transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.15s ease',
    position: 'relative'
  });

  const dropdownPanelStyle = {
    position: 'absolute',
    top: '48px',
    left: 0,
    backgroundColor: COLORS.PANEL_BG,
    borderBottom: `1px solid ${COLORS.BORDER}`,
    boxShadow: '0 4px 12px rgba(0,47,101,0.08)',
    zIndex: 999,
    display: 'flex',
    gap: '24px',
    padding: '16px 24px',
    minWidth: '280px'
  };

  const dropdownItemStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '6px 0',
    cursor: 'pointer',
    fontSize: '13px',
    color: COLORS.TEXT,
    textAlign: 'left',
    display: 'block',
    width: '100%',
    transition: 'color 0.15s',
    fontWeight: '400'
  };

  return (
    <div ref={dropdownRef}>
      {/* Top nav bar — 48px height, white background, navy text */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: COLORS.PANEL_BG,
          borderBottom: `1px solid rgba(0,47,101,0.06)`,
          height: '48px',
          padding: '0 16px',
          gap: '0'
        }}
      >
        {/* Logo section */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0',
            paddingRight: '16px',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => onModuleSelect('hub')}
          title="Back to Module Hub"
        >
          <img
            src={OPTIMAL_LOGO_SRC}
            alt="Optimal"
            style={{ height: '26px', width: 'auto', marginRight: '2px' }}
          />
          <sup style={{ fontSize: '9px', fontWeight: '700', color: LB, marginLeft: '1px', lineHeight: 1 }}>
            {currentModule === 'rcm' || currentModule === 'optimalrcm' ? 'RCM' : 'AV'}
          </sup>

          {/* Divider */}
          <div
            style={{
              width: '1px',
              height: '20px',
              backgroundColor: 'rgba(0,47,101,0.12)',
              margin: '0 12px'
            }}
          />

          {/* ARaaS badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: DB,
              borderRadius: '2px',
              padding: '2px 6px'
            }}
          >
            <span
              style={{
                fontSize: '8px',
                fontWeight: '700',
                color: LB,
                letterSpacing: '0.5px'
              }}
            >
              ARaaS
            </span>
            <span
              style={{
                fontSize: '6px',
                fontWeight: '700',
                color: LB,
                position: 'relative',
                top: '-3px',
                marginLeft: '0.5px'
              }}
            >
              ®
            </span>
          </div>
        </div>

        {/* Horizontal nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0', flex: 1 }}>
          {Object.entries(moduleGroups).map(([groupLabel, group]) => {
            const hasSubmodules = group.modules && group.modules.length > 0;
            const isActive = !hasSubmodules && currentModule === group.id;
            const dropdownOpen = openDropdown === group.id && hasSubmodules;

            return (
              <div key={group.id} style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    if (hasSubmodules) {
                      setOpenDropdown(openDropdown === group.id ? null : group.id);
                    } else {
                      onModuleSelect(group.id);
                      setOpenDropdown(null);
                    }
                  }}
                  style={navLinkStyle(isActive, hasSubmodules)}
                  onMouseEnter={() => {
                    if (hasSubmodules && !dropdownOpen) {
                      setOpenDropdown(group.id);
                    }
                  }}
                  title={groupLabel}
                >
                  {groupLabel}
                  {hasSubmodules && (
                    <ChevronDown size={12} style={{ opacity: 0.7 }} />
                  )}
                </button>

                {/* Dropdown panel for sub-modules */}
                {dropdownOpen && (
                  <div style={dropdownPanelStyle}>
                    {group.modules.map((mod) => (
                      <button
                        key={mod.id}
                        onClick={() => {
                          onModuleSelect(mod.id);
                          setOpenDropdown(null);
                        }}
                        style={{
                          ...dropdownItemStyle,
                          color: currentModule === mod.id ? LB : COLORS.TEXT,
                          fontWeight: currentModule === mod.id ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = LB;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = currentModule === mod.id ? LB : COLORS.TEXT;
                        }}
                      >
                        {currentModule === mod.id && '● '}{mod.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right side — live indicator, user info, logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginLeft: 'auto' }}>
          {/* Live indicator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '11px',
              color: COLORS.SUCCESS,
              fontWeight: '600'
            }}
            title="System status: Live"
          >
            <div
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: COLORS.SUCCESS,
                animation: 'pulse 2s infinite'
              }}
            />
            LIVE
          </div>

          {/* User info */}
          {typeof window !== 'undefined' && window.oavUser && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: COLORS.NAVY,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
                title="User menu"
              >
                {(window.oavUser().fullName || window.oavUser().username || 'U').charAt(0).toUpperCase()}
              </div>

              {/* Sign out button */}
              <button
                onClick={() => {
                  if (window.oavLogout) window.oavLogout();
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: COLORS.TEXT_DIM,
                  border: `1px solid ${COLORS.BORDER}`,
                  padding: '5px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: '500',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap'
                }}
                title="Sign out"
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
                  e.target.style.borderColor = COLORS.TEAL;
                  e.target.style.color = COLORS.TEAL;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = COLORS.BORDER;
                  e.target.style.color = COLORS.TEXT_DIM;
                }}
              >
                Sign Out
              </button>
            </div>
          )}

          {/* Help button */}
          <button
            onClick={() => onShowHelp?.()}
            style={{
              backgroundColor: 'transparent',
              color: COLORS.TEXT_DIM,
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.15s'
            }}
            title="Help"
          >
            <HelpCircle size={18} />
          </button>
        </div>
      </div>

      {/* Pulse animation for live indicator */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// SUBBAR COMPONENT - Shows breadcrumb and module info
// ============================================================================
function SubBar({ currentModule, selectedItem, selectedSection }) {
  const moduleLabels = {
    'hub': 'Module Hub',
    'outcomes': 'Outcomes',
    'optimalav': 'Availability Analysis',
    'rcm': 'RCM',
    'weibull': 'Weibull Analysis',
    'alt': 'ALT (Accelerated Life)',
    'lcc': 'Life Cycle Cost',
    'process': 'Process Reliability',
    'enterprise': 'Enterprise Manager'
  };

  const sectionLabel = TREE_SECTIONS.find(s => s.id === selectedSection)?.label || selectedSection;
  const moduleLabel = moduleLabels[currentModule] || currentModule;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: COLORS.LIGHT_BG,
        borderBottom: `1px solid ${COLORS.BORDER}`,
        height: '44px',
        padding: '0 16px',
        gap: '8px',
        fontSize: '12px',
        color: COLORS.TEXT_DIM
      }}
    >
      <span style={{ fontWeight: '600', color: COLORS.NAVY }}>Optimal</span>
      <span>›</span>
      <span style={{ fontWeight: '600', color: COLORS.NAVY }}>{moduleLabel}</span>
      {selectedSection && selectedSection !== 'hub' && (
        <>
          <span>›</span>
          <span style={{ fontWeight: '600', color: COLORS.NAVY }}>{sectionLabel}</span>
        </>
      )}
      {selectedItem && selectedSection !== 'hub' && (
        <>
          <span>›</span>
          <span style={{ color: COLORS.TEXT }}>{selectedItem.id || selectedItem.description}</span>
        </>
      )}
    </div>
  );
}

// ============================================================================
// MAIN APP COMPONENT (PART 1)
// ============================================================================
export function AppPart1() {
  // State management
  const [data, setData, undo, redo, canUndo, canRedo] = useUndoRedo(createDemoData());
  const [selectedTreeSection, setSelectedTreeSection] = useState('equipments');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedGridRows, setSelectedGridRows] = useState([]);
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [currentModule, setCurrentModule] = useState('optimalav');
  const [simulationRunning, setSimulationRunning] = useState(false);

  // Handle item selection
  const handleSelectItem = useCallback((item) => {
    setSelectedItem(item);
    setSelectedGridRows([item.id]);
  }, []);

  // Handle add item
  const handleAddItem = useCallback((sectionId) => {
    const timestamp = Date.now();
    const newItem = {
      id: `${sectionId.toUpperCase().slice(0, 3)}-${timestamp.toString().slice(-6)}`,
      description: 'New ' + sectionId,
      ...Object.fromEntries(
        COLUMN_DEFINITIONS[sectionId]?.map(col => [col.key, null]) || []
      )
    };

    setData({
      ...data,
      [sectionId]: [...(data[sectionId] || []), newItem]
    });

    setSelectedItem(newItem);
  }, [data, setData]);

  // Handle delete item
  const handleDeleteItem = useCallback(() => {
    if (!selectedItem) return;

    setData({
      ...data,
      [selectedTreeSection]: data[selectedTreeSection].filter(item => item.id !== selectedItem.id)
    });

    setSelectedItem(null);
  }, [selectedItem, selectedTreeSection, data, setData]);

  // Handle edit cell
  const handleEditCell = useCallback((rowId, colKey, value) => {
    setData({
      ...data,
      [selectedTreeSection]: data[selectedTreeSection].map(item =>
        item.id === rowId ? { ...item, [colKey]: value } : item
      )
    });
  }, [selectedTreeSection, data, setData]);

  // Handle property update
  const handlePropertyUpdate = useCallback((updated) => {
    setData({
      ...data,
      [selectedTreeSection]: data[selectedTreeSection].map(item =>
        item.id === updated.id ? updated : item
      )
    });
    setSelectedItem(updated);
  }, [selectedTreeSection, data, setData]);

  // Handle zoom
  const handleZoom = useCallback((factor) => {
    if (factor === 'fit') {
      setZoomLevel(1);
    } else {
      setZoomLevel(prev => Math.max(0.2, Math.min(5, prev * factor)));
    }
  }, []);

  // Handle simulation
  const handleRunSimulation = useCallback(() => {
    setSimulationRunning(true);
    console.log('Simulation started with data:', data);
    // Simulation engine would run here
  }, [data]);

  const handleStopSimulation = useCallback(() => {
    setSimulationRunning(false);
    console.log('Simulation stopped');
  }, []);

  // Get current section items
  const currentSectionItems = data[selectedTreeSection] || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: COLORS.LIGHT_BG }}>
      {/* New hybrid top-nav bar */}
      <TopNav
        currentModule={currentModule}
        onModuleSelect={setCurrentModule}
        onShowHelp={() => console.log('Help')}
      />

      {/* Subbar with breadcrumb (only show when not on hub) */}
      {currentModule !== 'hub' && (
        <SubBar
          currentModule={currentModule}
          selectedItem={selectedItem}
          selectedSection={selectedTreeSection}
        />
      )}

      {/* Quick toolbar with edit operations */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: COLORS.PANEL_BG,
          borderBottom: `1px solid ${COLORS.BORDER}`,
          alignItems: 'center'
        }}
      >
        <button
          onClick={() => setData(createDemoData())}
          style={{
            backgroundColor: 'transparent',
            color: COLORS.TEXT,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s'
          }}
          title="New project"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
            e.target.style.color = COLORS.TEAL;
            e.target.style.borderColor = COLORS.TEAL;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.TEXT;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <File size={14} /> New
        </button>

        <button
          onClick={() => console.log('Open')}
          style={{
            backgroundColor: 'transparent',
            color: COLORS.TEXT,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s'
          }}
          title="Open project"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
            e.target.style.color = COLORS.TEAL;
            e.target.style.borderColor = COLORS.TEAL;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.TEXT;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <FolderOpen size={14} /> Open
        </button>

        <button
          onClick={() => console.log('Save')}
          style={{
            backgroundColor: 'transparent',
            color: COLORS.TEXT,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s'
          }}
          title="Save project"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
            e.target.style.color = COLORS.TEAL;
            e.target.style.borderColor = COLORS.TEAL;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.TEXT;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <Save size={14} /> Save
        </button>

        {/* Separator */}
        <div style={{ width: '1px', height: '20px', backgroundColor: COLORS.BORDER, margin: '0 4px' }} />

        <button
          onClick={undo}
          disabled={!canUndo}
          style={{
            backgroundColor: 'transparent',
            color: canUndo ? COLORS.TEXT : COLORS.TEXT_DIM,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: canUndo ? 'pointer' : 'not-allowed',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: canUndo ? 1 : 0.4,
            transition: 'all 0.15s'
          }}
          title="Undo"
          onMouseEnter={(e) => {
            if (canUndo) {
              e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
              e.target.style.color = COLORS.TEAL;
              e.target.style.borderColor = COLORS.TEAL;
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = canUndo ? COLORS.TEXT : COLORS.TEXT_DIM;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <RotateCcw size={14} />
        </button>

        <button
          onClick={redo}
          disabled={!canRedo}
          style={{
            backgroundColor: 'transparent',
            color: canRedo ? COLORS.TEXT : COLORS.TEXT_DIM,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: canRedo ? 'pointer' : 'not-allowed',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: canRedo ? 1 : 0.4,
            transition: 'all 0.15s'
          }}
          title="Redo"
          onMouseEnter={(e) => {
            if (canRedo) {
              e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
              e.target.style.color = COLORS.TEAL;
              e.target.style.borderColor = COLORS.TEAL;
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = canRedo ? COLORS.TEXT : COLORS.TEXT_DIM;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <RotateCw size={14} />
        </button>

        {/* Separator */}
        <div style={{ width: '1px', height: '20px', backgroundColor: COLORS.BORDER, margin: '0 4px' }} />

        <button
          onClick={() => handleAddItem(selectedTreeSection)}
          style={{
            backgroundColor: 'transparent',
            color: COLORS.TEXT,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s'
          }}
          title="Add new item"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
            e.target.style.color = COLORS.TEAL;
            e.target.style.borderColor = COLORS.TEAL;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.TEXT;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <Plus size={14} />
        </button>

        <button
          onClick={handleDeleteItem}
          disabled={!selectedItem}
          style={{
            backgroundColor: 'transparent',
            color: selectedItem ? COLORS.TEXT : COLORS.TEXT_DIM,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: selectedItem ? 'pointer' : 'not-allowed',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: selectedItem ? 1 : 0.4,
            transition: 'all 0.15s'
          }}
          title="Delete selected item"
          onMouseEnter={(e) => {
            if (selectedItem) {
              e.target.style.backgroundColor = '#FFE5E5';
              e.target.style.color = '#C0392B';
              e.target.style.borderColor = '#C0392B';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = selectedItem ? COLORS.TEXT : COLORS.TEXT_DIM;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <Trash2 size={14} />
        </button>

        {/* Separator */}
        <div style={{ width: '1px', height: '20px', backgroundColor: COLORS.BORDER, margin: '0 4px' }} />

        <button
          onClick={handleRunSimulation}
          style={{
            backgroundColor: 'transparent',
            color: COLORS.SUCCESS,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s'
          }}
          title="Run simulation"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
            e.target.style.borderColor = COLORS.TEAL;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <Play size={14} />
        </button>

        <button
          onClick={handleStopSimulation}
          disabled={!simulationRunning}
          style={{
            backgroundColor: 'transparent',
            color: simulationRunning ? COLORS.TEXT : COLORS.TEXT_DIM,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: simulationRunning ? 'pointer' : 'not-allowed',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: simulationRunning ? 1 : 0.4,
            transition: 'all 0.15s'
          }}
          title="Stop simulation"
          onMouseEnter={(e) => {
            if (simulationRunning) {
              e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
              e.target.style.color = COLORS.TEAL;
              e.target.style.borderColor = COLORS.TEAL;
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = simulationRunning ? COLORS.TEXT : COLORS.TEXT_DIM;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <Square size={14} />
        </button>

        {/* Separator */}
        <div style={{ width: '1px', height: '20px', backgroundColor: COLORS.BORDER, margin: '0 4px' }} />

        <button
          onClick={() => handleZoom(1.2)}
          style={{
            backgroundColor: 'transparent',
            color: COLORS.TEXT,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s'
          }}
          title="Zoom in"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
            e.target.style.color = COLORS.TEAL;
            e.target.style.borderColor = COLORS.TEAL;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.TEXT;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <ZoomIn size={14} />
        </button>

        <button
          onClick={() => handleZoom(0.8)}
          style={{
            backgroundColor: 'transparent',
            color: COLORS.TEXT,
            border: `1px solid ${COLORS.BORDER}`,
            padding: '5px 10px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.15s'
          }}
          title="Zoom out"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.TEAL_LIGHT;
            e.target.style.color = COLORS.TEAL;
            e.target.style.borderColor = COLORS.TEAL;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = COLORS.TEXT;
            e.target.style.borderColor = COLORS.BORDER;
          }}
        >
          <ZoomOut size={14} />
        </button>

        {/* Status info */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: COLORS.TEXT_DIM }}>
          <span>Zoom: {Math.round(zoomLevel * 100)}%</span>
          {simulationRunning && (
            <span style={{ color: COLORS.SUCCESS, fontWeight: '600' }}>● Simulation running...</span>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Panel - Tree */}
        {showLeftPanel && (
          <div
            style={{
              width: '280px',
              borderRight: `1px solid ${COLORS.BORDER}`,
              backgroundColor: COLORS.PANEL_BG,
              overflowY: 'auto',
              padding: '8px'
            }}
          >
            {/* Search across all items */}
            <input
              type="text"
              placeholder="Search all..."
              style={{
                width: '100%',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                padding: '6px 8px',
                borderRadius: '3px',
                fontSize: '12px',
                marginBottom: '12px',
                boxSizing: 'border-box'
              }}
            />

            {/* Tree Sections */}
            {TREE_SECTIONS.map(section => (
              <TreeSection
                key={section.id}
                section={section}
                items={data[section.id] || []}
                selectedItem={selectedItem}
                onSelectItem={(item) => {
                  handleSelectItem(item);
                  setSelectedTreeSection(section.id);
                }}
                onAddItem={handleAddItem}
                onDeleteItem={handleDeleteItem}
              />
            ))}
          </div>
        )}

        {/* Center Panel - Grid */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: COLORS.LIGHT_BG }}>
          {/* Grid Title */}
          <div
            style={{
              padding: '8px 12px',
              borderBottom: `1px solid ${COLORS.BORDER}`,
              backgroundColor: COLORS.PANEL_BG,
              color: COLORS.TEXT,
              fontSize: '13px',
              fontWeight: '600'
            }}
          >
            {TREE_SECTIONS.find(s => s.id === selectedTreeSection)?.label || selectedTreeSection}
          </div>

          {/* Data Grid */}
          <DataGrid
            data={currentSectionItems}
            columns={COLUMN_DEFINITIONS[selectedTreeSection] || []}
            selectedRows={selectedGridRows}
            onSelectRows={setSelectedGridRows}
            onEditCell={handleEditCell}
            onAddRow={() => handleAddItem(selectedTreeSection)}
            onDeleteRow={() => selectedGridRows.forEach(rowId => {
              setData({
                ...data,
                [selectedTreeSection]: data[selectedTreeSection].filter(item => item.id !== rowId)
              });
            })}
          />
        </div>

        {/* Right Panel - Properties */}
        {showRightPanel && (
          <div
            style={{
              width: '320px',
              borderLeft: `1px solid ${COLORS.BORDER}`,
              backgroundColor: COLORS.PANEL_BG,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              style={{
                padding: '8px 12px',
                borderBottom: `1px solid ${COLORS.BORDER}`,
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              Properties
            </div>
            <PropertyInspector
              selectedItem={selectedItem}
              entityType={selectedTreeSection}
              onUpdate={handlePropertyUpdate}
              allData={data}
            />
          </div>
        )}
      </div>

      {/* Status Bar — navy footer matching Spares */}
      <div
        style={{
          borderTop: `1px solid ${COLORS.BORDER}`,
          padding: '8px 16px',
          backgroundColor: COLORS.NAVY,
          color: '#FFFFFF',
          fontSize: '11px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span>
          {selectedItem ? `Selected: ${selectedItem.id}` : 'Ready'}
        </span>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          {currentModule !== 'hub' && selectedTreeSection && (
            <span>Section: {TREE_SECTIONS.find(s => s.id === selectedTreeSection)?.label || selectedTreeSection}</span>
          )}
          {simulationRunning && (
            <span style={{ color: COLORS.SUCCESS, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
              ● Simulation active
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT EXPORTS
// ============================================================================
export { MenuBar, Toolbar, TreeSection, DataGrid, PropertyInspector, TopNav, SubBar };
export { useUndoRedo, createDemoData, createBlankProject, COLORS, TREE_SECTIONS, TREE_GROUPS, COLUMN_DEFINITIONS };
